import type { ChapterData } from "./content-types";

/**
 * CAPÍTULO 5 — "Nadie vio nada" (Distrito Gris, riesgos psicosociales).
 * Mecánica distintiva: DECISIÓN de actuación ante el acoso.
 * Misión SIN EPIs. Tema sensible: el acoso nunca es culpa de la víctima.
 */
export const GRIS: ChapterData = {
  id: "gris",
  title: "Nadie vio nada",
  location: "gris",
  secStart: 40,
  secGreat: 82,
  secOk: 68,
  secBad: 58,
  needTalk: 2,
  needHazards: 3,

  interactables: [
    { id: "caso", type: "machine", pos: { x: 7, y: 6 }, label: "Decidir cómo actuar", verb: "use", ref: "caso", radius: 1.7, color: 0x6a6a72 },
    { id: "correos", type: "object", pos: { x: 5, y: 3 }, label: "Reuniones y correos", verb: "read", ref: "correos", radius: 1.4, color: 0xeef0f3 },
    { id: "mensajes", type: "object", pos: { x: 3, y: 4 }, label: "Mensajes a deshora", verb: "inspect", ref: "mensajes", radius: 1.3, color: 0x2f373c },
    { id: "carga", type: "object", pos: { x: 9, y: 3 }, label: "Reparto de la carga", verb: "inspect", ref: "carga", radius: 1.4, color: 0xc3c8d0 },
    { id: "protocolo", type: "object", pos: { x: 11, y: 3 }, label: "Protocolo de acoso", verb: "inspect", ref: "protocolo", radius: 1.4, color: 0xb7bcc4 },
    { id: "buzon", type: "object", pos: { x: 12.5, y: 5 }, label: "Buzón de sugerencias", verb: "inspect", ref: "buzon", radius: 1.3, color: 0x7a8290 },
    { id: "bajas", type: "object", pos: { x: 10, y: 5 }, label: "Bajas y rotación", verb: "read", ref: "bajas", radius: 1.4, color: 0xeef0f3 },
    { id: "evaluacion", type: "document", pos: { x: 2, y: 6 }, label: "Evaluación psicosocial", verb: "read", ref: "evaluacion", radius: 1.4, color: 0xc9a06a },
    { id: "medidas", type: "machine", pos: { x: 12.5, y: 8 }, label: "Poner en marcha las medidas", verb: "use", ref: "medidas", radius: 1.6, color: 0x2f8f57 },
    { id: "elena", type: "npc", pos: { x: 4, y: 8 }, label: "Elena", verb: "talk", ref: "elena", radius: 1.5, color: 0x6a6a72 },
    { id: "pablo", type: "npc", pos: { x: 7, y: 8 }, label: "Pablo", verb: "talk", ref: "pablo", radius: 1.5, color: 0x5a5f68 },
    { id: "sonia", type: "npc", pos: { x: 10, y: 8 }, label: "Sonia", verb: "talk", ref: "sonia", radius: 1.5, color: 0x565663 },
    { id: "vega", type: "npc", pos: { x: 13, y: 9 }, label: "Vega", verb: "talk", ref: "vega", radius: 1.5, color: 0x2f2f36 },
    { id: "salida", type: "door", pos: { x: 13, y: 10 }, label: "Salir a la ciudad", verb: "open", radius: 1.7 },
  ],

  hotspots: {
    caso: {
      label: "Decidir cómo actuar",
      text: "Elena se ha decidido a contártelo. Lo que hagas ahora importa. <b>No hay respuestas neutras.</b>",
      comment: "Escuchar, activar el protocolo y no dejarla sola. Eso rompe el 'nadie vio nada'.",
    },
    correos: {
      label: "Reuniones y correos",
      text: "A <b>Elena</b> la han <b>dejado fuera</b> de reuniones y de copias de correo. Se entera de todo tarde y mal. Eso es <b>aislamiento</b>.",
      note: { section: "pistas", heading: "Aislamiento en el equipo", body: "Excluir a alguien de reuniones y comunicaciones es una forma de acoso: aislamiento. No es 'un despiste'." },
      hazardId: "h_aislamiento",
      comment: "A Elena la borran de reuniones y correos. Casualidad una vez; costumbre, ya no.",
    },
    mensajes: {
      label: "Mensajes a deshora",
      text: "El móvil de trabajo: <b>mensajes a las 2 de la madrugada</b>, en festivos, exigiendo respuesta ya. Sin <b>derecho a la desconexión</b>, el trabajo no termina nunca.",
      note: { section: "pistas", heading: "Sin derecho a la desconexión", body: "Mensajes de trabajo a cualquier hora que exigen respuesta inmediata. La desconexión digital es un derecho y previene el tecnoestrés." },
      hazardId: "h_tecnoestres",
      comment: "Mensajes a las dos de la mañana. Si el trabajo nunca se apaga, la cabeza tampoco.",
      assess: {
        identify: {
          q: "¿Qué riesgo generan los mensajes de trabajo a deshora?",
          options: [
            { text: "Tecnoestrés por no poder desconectar del trabajo nunca", ok: true },
            { text: "Riesgo eléctrico del móvil", ok: false },
            { text: "Fatiga visual de la pantalla", ok: false },
          ],
          hint: "Mensajes a las 2 de la madrugada exigiendo respuesta. Piensa qué le pasa al descanso si el trabajo no se apaga nunca.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Acordar el derecho a la desconexión para todo el equipo: nada exigible fuera de jornada", tier: "organizativa", grade: "best", why: "Actuar sobre la organización (no exigir respuesta fuera de jornada) elimina la fuente del tecnoestrés para todos. Es la medida de fondo." },
            { text: "Fijar franjas claras de disponibilidad y respetar el horario", tier: "organizativa", grade: "ok", why: "Poner límites ayuda, pero si se sigue escribiendo y respondiendo fuera de ellos, la presión continúa: hace falta el acuerdo firme." },
            { text: "Que cada cual silencie el móvil si no quiere leerlos", tier: "ninguna", grade: "bad", why: "Traslada el problema a la persona; la solución es organizativa y para todo el equipo.", danger: "Quien silencia el móvil teme perderse algo 'urgente' y acaba mirándolo igual: la presión sigue." },
          ],
        },
        apply: {
          intro: "Implanta el derecho a la desconexión. Haz los pasos en orden.",
          steps: [
            "Acordar por escrito el derecho a la desconexión",
            "Definir que no hay respuesta exigible fuera de jornada",
            "Comunicarlo a toda la plantilla y a los mandos",
            "Revisar periódicamente que se cumple",
          ],
          wrong: "Ese paso todavía no. Primero se acuerda y se define; luego se comunica y se revisa que de verdad se respeta.",
          success: "Desconexión acordada, comunicada y revisada: el trabajo por fin tiene un final cada día.",
        },
        note: { section: "expedientes", heading: "Derecho a la desconexión", body: "El tecnoestrés se previene actuando sobre la organización (desconexión digital para todo el equipo), no pidiendo a cada persona que se defienda sola." },
      },
    },
    carga: {
      label: "Reparto de la carga",
      text: "Casi todo cae sobre <b>una sola persona</b>, con objetivos imposibles, mientras el resto va sobrado. Sobrecarga sostenida y sin apoyo.",
      note: { section: "pistas", heading: "Sobrecarga desigual", body: "Concentrar la carga y objetivos inalcanzables en una persona es un riesgo psicosocial. La carga debe repartirse." },
      hazardId: "h_sobrecarga",
      comment: "Una persona con el triple de trabajo y objetivos de ciencia ficción. Así se quema a cualquiera.",
    },
    protocolo: {
      label: "Protocolo de acoso",
      text: "El tablón. Donde debería estar el <b>protocolo de acoso</b> y el canal de denuncia, solo hay una chincheta y un rectángulo más limpio. Lo quitaron.",
      note: { section: "pistas", heading: "Sin protocolo visible", body: "La empresa debe tener un protocolo de acoso conocido y un canal seguro para denunciar. Aquí no está a la vista." },
      hazardId: "h_protocolo",
      comment: "El protocolo de acoso... desaparecido del tablón. Justo lo que debería estar más a mano.",
      assess: {
        identify: {
          q: "¿Qué supone que no haya protocolo de acoso ni canal visibles?",
          options: [
            { text: "Que quien sufra acoso no sepa cómo ni a dónde acudir de forma segura", ok: true },
            { text: "Un incumplimiento de la normativa de incendios", ok: false },
            { text: "Un descuido administrativo sin importancia", ok: false },
          ],
          hint: "Donde debería estar el protocolo y el canal solo hay una chincheta. Piensa qué le pasa a quien necesita ayuda y no encuentra a dónde ir.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Reponer el protocolo y el canal seguro, y darlos a conocer a toda la plantilla", tier: "organizativa", grade: "best", why: "El protocolo solo protege si se conoce: visible, con un canal seguro y difundido a todo el equipo. Es la medida de fondo." },
            { text: "Guardarlo en una carpeta por si alguien lo pide", tier: "organizativa", grade: "ok", why: "Tenerlo escrito es un mínimo, pero un protocolo escondido no lo usa nadie: tiene que estar accesible y conocido." },
            { text: "Dejarlo, total ya se sabe a quién acudir", tier: "ninguna", grade: "bad", why: "No todo el mundo lo sabe, y menos quien más lo necesita.", danger: "Quien sufre acoso no encuentra a dónde acudir con seguridad y calla: el problema sigue oculto." },
          ],
        },
        apply: {
          intro: "Pon el protocolo de acoso a disposición de todos. Haz los pasos en orden.",
          steps: [
            "Reponer el protocolo de acoso en el tablón",
            "Habilitar un canal seguro y confidencial",
            "Informar a toda la plantilla de cómo y a dónde acudir",
            "Formar a los mandos en su aplicación",
          ],
          wrong: "Ese paso todavía no. Primero el protocolo y el canal existen y son seguros; después se difunde y se forma.",
          success: "Protocolo visible, canal seguro y todos informados: quien lo necesite sabe a dónde acudir.",
        },
        note: { section: "expedientes", heading: "Protocolo visible y conocido", body: "El protocolo de acoso y su canal deben ser conocidos por la plantilla y estar accesibles. Un protocolo que no se ve no cumple su función." },
      },
    },
    buzon: {
      label: "Buzón de sugerencias",
      text: "El <b>buzón de sugerencias</b>, rebosando de papeles sin abrir. La gente avisó. Nadie respondió. La <b>comunicación</b> está rota.",
      note: { section: "pistas", heading: "Comunicación rota", body: "Cuando los avisos del equipo no obtienen respuesta, la participación se apaga. Hacen falta cauces reales de comunicación." },
      hazardId: "h_comunicacion",
      comment: "Un buzón lleno de avisos sin abrir. La gente habló; alguien decidió no escuchar.",
      assess: {
        identify: {
          q: "¿Qué riesgo revela un buzón de avisos sin abrir desde hace meses?",
          options: [
            { text: "Comunicación rota: los problemas no se detectan porque no se escucha", ok: true },
            { text: "Riesgo de incendio por acumulación de papel", ok: false },
            { text: "Un problema de motivación individual del trabajador", ok: false },
          ],
          hint: "La gente avisó y nadie respondió. Piensa qué pasa con los problemas de un equipo cuando sus avisos caen en saco roto.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Leer los avisos, responder y crear un cauce real de participación", tier: "organizativa", grade: "best", why: "Escuchar y devolver respuesta reactiva la participación: la comunicación es prevención, y detectar a tiempo evita que los problemas crezcan." },
            { text: "Vaciar el buzón y empezar de cero sin leer nada", tier: "ninguna", grade: "bad", why: "Tirar los avisos sin leerlos manda el mensaje de que hablar no sirve y pierde información valiosa.", danger: "La gente ve que sus avisos acaban en la basura y deja de avisar: los problemas se enquistan en silencio." },
            { text: "Dejarlo como está, ya escribirán si es importante", tier: "ninguna", grade: "bad", why: "Ya escribieron y nadie respondió; ese silencio es parte del problema.", danger: "El equipo aprende que avisar no cambia nada y se apaga la participación." },
          ],
        },
        apply: {
          intro: "Reactiva la comunicación del equipo. Haz los pasos en orden.",
          steps: [
            "Leer todos los avisos acumulados en el buzón",
            "Responder y dar seguimiento a cada uno",
            "Establecer un cauce de participación periódico",
            "Comunicar al equipo las mejoras aplicadas",
          ],
          wrong: "Ese paso todavía no. Primero se leen y se responden los avisos; luego se crea el cauce estable y se comunica.",
          success: "Avisos leídos y respondidos, con un cauce estable: la gente vuelve a hablar porque ahora sirve.",
        },
        note: { section: "expedientes", heading: "Cauces reales de comunicación", body: "Los canales de participación solo funcionan si se leen y se responde. Sin respuesta, la gente deja de avisar y los problemas se enquistan." },
      },
    },
    bajas: {
      label: "Bajas y rotación",
      text: "El registro de personal: <b>bajas repetidas</b> y gente que pide el traslado. Cuando un equipo 'quema' a todo el que pasa, el problema es el entorno.",
      note: { section: "expedientes", heading: "Indicadores de alarma", body: "Absentismo y rotación altos en un mismo equipo son señales de un problema psicosocial de organización, no de las personas." },
      hazardId: "h_indicadores",
      comment: "Todo el que pasa por este equipo acaba de baja o pidiendo el traslado. El patrón habla solo.",
    },
    evaluacion: {
      label: "Evaluación psicosocial",
      text: "La <b>evaluación de riesgos psicosociales</b>, marcada como <b>'pendiente'</b> desde hace más de un año. Sin evaluar, todo esto queda invisible.",
      note: { section: "expedientes", heading: "Sin evaluación psicosocial", body: "Los riesgos psicosociales deben evaluarse como cualquier otro. Sin evaluación no hay medidas." },
      giveItem: "eval_psico",
      comment: "La evaluación psicosocial, 'pendiente' un año entero. Lo que no se mide, no se arregla.",
    },
  },

  npcs: {
    elena: {
      name: "Elena",
      role: "Del equipo",
      portrait: "elena",
      note: { section: "personas", heading: "Elena", body: "La persona sobre la que recae todo: exclusión, sobrecarga y 'bromas'. Empieza a dudar de sí misma. No es su culpa." },
      dialogue: [
        { speaker: "npc", text: "No sé si merece la pena contarlo... a lo mejor soy yo, que no lo llevo bien." },
        { speaker: "epi", text: "Cuéntamelo. Y para empezar: si esto está pasando, no es culpa tuya." },
        { speaker: "npc", text: "Me dejan fuera de todo, me cargan lo que nadie quiere, y luego 'son bromas'. Contesto correos a las tantas por miedo. Y cuando lo dije, me miraron como si exagerara.", flag: "elena_relato", note: { section: "pistas", heading: "Relato de Elena", body: "Exclusión, sobrecarga, 'bromas' y presión fuera de horario. Cuando lo comunicó, la minimizaron. Patrón claro de acoso." }, end: true },
      ],
    },
    pablo: {
      name: "Pablo",
      role: "Compañero",
      portrait: "pablo",
      note: { section: "personas", heading: "Pablo", body: "Lo vio todo y calló por miedo. El 'nadie vio nada' que permite que siga pasando." },
      dialogue: [
        { speaker: "npc", text: "Yo no quiero líos, ¿eh? Yo no he visto nada." },
        { speaker: "epi", text: "Pablo, lo has visto. Callar también es una decisión." },
        { speaker: "npc", text: "...vale. Sí lo vi. Las bromas, los correos de noche, cómo la dejaban fuera. Callé por miedo a que me tocara a mí. Pero así nunca para, ¿verdad?", flag: "pablo_testigo", note: { section: "pistas", heading: "El silencio de los testigos", body: "El silencio del entorno ('nadie vio nada') es lo que permite que el acoso continúe. Apoyar a la persona rompe el patrón." }, end: true },
      ],
    },
    sonia: {
      name: "Sonia",
      role: "Responsable",
      portrait: "sonia",
      note: { section: "personas", heading: "Sonia", body: "Responsable que minimiza ('cosas de compañeros'). La 'optimización' suprimió el delegado y la evaluación psicosocial." },
      dialogue: [
        { speaker: "npc", text: "Uy, eso son cosas de compañeros, roces normales. No lo agrandemos, ¿eh?" },
        { speaker: "epi", text: "¿'Roces'? Hay exclusión, sobrecarga y una evaluación sin hacer. Eso no es un roce." },
        { choices: [ { text: "¿Dónde está el protocolo de acoso?", go: 3 }, { text: "¿Y la evaluación psicosocial?", go: 4 } ] },
        { speaker: "npc", text: "El protocolo... estaba en el tablón, sí. Cuando fusionaron los equipos por la 'optimización' quitaron al delegado de prevención, y con él se descolgó todo. Nadie lo repuso. Preferimos no remover, la verdad.", flag: "sonia_optimizacion", note: { section: "pistas", heading: "La 'optimización' suprimió la prevención", body: "La reorganización eliminó al delegado de prevención y el protocolo dejó de estar visible. Minimizar es parte del problema." }, end: true },
        { speaker: "npc", text: "Pendiente. Lleva más de un año en 'pendiente'. Entró en la misma lista de recortes que se llevó al delegado. Sin evaluar, esto no existe en ningún papel... y así es más cómodo para todos menos para quien lo sufre.", flag: "sonia_optimizacion", note: { section: "pistas", heading: "La 'optimización' suprimió la prevención", body: "La evaluación psicosocial lleva más de un año sin hacerse, recortada junto al delegado. Sin evaluación, el riesgo queda invisible." }, end: true },
      ],
    },
    vega: {
      name: "Vega",
      role: "¿?",
      portrait: "vega",
      note: { section: "personas", heading: "Vega", body: "Casi al final del rastro. La lista de 'optimizaciones' está casi completa." },
      dialogue: [
        { speaker: "npc", text: "Cero cinco. El más difícil de ver, porque no deja marca en la pared: la deja en las personas." },
        { speaker: "epi", text: "Todas las tarjetas llevan a lo mismo, ¿no?" },
        { speaker: "npc", text: "A una firma. Alguien recorta prevención en toda la ciudad y lo llama 'optimizar'. Te falta un número para tenerlo. Y creo que ya intuyes quién.", flag: "vega_firma", note: { section: "pistas", heading: "Una sola firma", body: "Todas las 'optimizaciones' (03, 05, 07, 09, 11) las firma la misma persona, que recorta prevención por ahorro." }, end: true },
      ],
    },
  },

  hazards: [
    { id: "h_aislamiento", category: "Psicosocial: aislamiento", severity: 3, discoverable: true, requiredAction: "Romper el aislamiento; protocolo de acoso" },
    { id: "h_sobrecarga", category: "Psicosocial: carga", severity: 3, discoverable: true, requiredAction: "Repartir la carga; objetivos realistas" },
    { id: "h_protocolo", category: "Falta de protocolo", severity: 2, discoverable: true, requiredAction: "Activar y dar a conocer el protocolo" },
    { id: "h_tecnoestres", category: "Tecnoestrés", severity: 2, discoverable: true, requiredAction: "Derecho a la desconexión" },
    { id: "h_comunicacion", category: "Comunicación", severity: 1, discoverable: true, requiredAction: "Cauces de comunicación y participación" },
    { id: "h_indicadores", category: "Indicadores", severity: 2, discoverable: true, requiredAction: "Atender bajas y rotación" },
    { id: "h_evaluacion", category: "Sin evaluación", severity: 2, discoverable: true, requiredAction: "Evaluar riesgos psicosociales" },
  ],

  mission: {
    id: "cap5_nadie_vio_nada",
    title: "Nadie vio nada",
    location: "gris",
    objectives: [
      { id: "talk", type: "talk_workers", label: "Habla con el equipo (con respeto)", target: "2" },
      { id: "hazards", type: "discover_hazards", label: "Detecta los riesgos psicosociales", target: "3" },
      { id: "extra", type: "extra", label: "Decide cómo actuar con Elena", hidden: true },
      { id: "report", type: "report", label: "Pon en marcha las medidas", hidden: true },
    ],
  },

  epi: { needed: [], optional: [], overkill: [] },

  extra: {
    triggerId: "caso",
    type: "decision",
    label: "¿Cómo actúas?",
    intro: "Elena se decide a contarte lo que pasa. Lo que hagas ahora importa. No hay respuestas 'neutras'.",
    dilemas: [
      {
        q: "Elena te cuenta que la excluyen y la sobrecargan, y duda de sí misma. ¿Qué haces primero?",
        options: [
          { text: "Escucharla, creerle y dejarle claro que no es culpa suya.", ok: true, why: "Correcto. Escuchar sin juzgar y validar a la persona rompe el aislamiento: es el primer paso." },
          { text: "Quitarle hierro: 'son bromas, no le des importancia'.", ok: false, why: "Minimizar es parte del problema: hace que la persona calle y dude aún más de sí misma." },
          { text: "Ir a encarar al grupo delante de todos en ese momento.", ok: false, why: "Exponerla en público puede empeorar la situación y las represalias. Primero, protección y cauces." },
        ],
      },
      {
        q: "¿Cómo lo canalizas?",
        options: [
          { text: "Activar el protocolo de acoso y avisar al delegado/a de prevención.", ok: true, why: "Correcto. Existe un procedimiento: hay que activarlo y apoyarse en la representación de prevención." },
          { text: "Contarlo en el grupo del equipo para que 'se enteren todos'.", ok: false, why: "Rompe la confidencialidad y expone a la persona. El caso se trata por el canal, no en público." },
          { text: "Esperar a ver si se arregla solo con el tiempo.", ok: false, why: "El acoso no se resuelve solo: sin intervenir, se enquista y crece." },
        ],
      },
      {
        q: "¿Qué le pides a la empresa?",
        options: [
          { text: "Evaluación de riesgos psicosociales y reparto real de la carga.", ok: true, why: "Correcto. Se actúa sobre la organización (la causa), no sobre la persona que lo sufre." },
          { text: "Cambiar a Elena de equipo para 'quitarla de en medio'.", ok: false, why: "Mover a la víctima la penaliza a ella y deja intacto el problema." },
          { text: "Apuntar a Elena a un curso de 'gestión del estrés' y ya.", ok: false, why: "Traslada la culpa a la persona. El estrés viene de la organización; ahí hay que intervenir." },
        ],
      },
      {
        q: "¿Y el registro y la protección?",
        options: [
          { text: "Documentar los hechos garantizando confidencialidad y sin represalias.", ok: true, why: "Correcto. Registrar con confidencialidad y proteger frente a represalias es clave." },
          { text: "Pedir a los testigos que declaren en público en una reunión.", ok: false, why: "Exponer a los testigos genera miedo y represalias. La confidencialidad protege a todos." },
          { text: "No dejar nada por escrito para 'no montar lío'.", ok: false, why: "Sin registro no hay caso ni protección. Documentar es lo que permite actuar." },
        ],
      },
    ],
    wrong: "Alguna decisión no protege a la persona o traslada la culpa. Recuerda: escuchar y creer, activar el protocolo, actuar sobre la organización (no sobre la víctima) y documentar con confidencialidad.",
    success: "Escuchaste y creíste a Elena, activaste el protocolo con el delegado de prevención, pediste evaluar y repartir la carga, y lo documentaste con confidencialidad. Así se rompe el 'nadie vio nada'.",
    note: { section: "expedientes", heading: "Actuación ante el acoso", body: "Escuchar y creer sin culpar, activar el protocolo y avisar a prevención, actuar sobre la organización, documentar con confidencialidad y sin represalias." },
  },

  consequence: {
    title: "Actuar",
    lines: {},
    great: "Con el protocolo activo y la organización arreglada, Elena deja de estar sola.",
    ok: "",
    bad: "",
  },

  workId: "medidas",
  reward: "tarjeta09",
  resolveNotes: [
    { section: "pistas", heading: "Tarjeta 09 y la firma", body: "En el expediente de 'optimización' aparece la tarjeta 09 y una firma legible al pie. La lista: 03, 05, 07, 09, 11." },
    { section: "expedientes", heading: "Caso: Nadie vio nada", body: "Acoso, aislamiento y sobrecarga por falta de evaluación psicosocial y protocolo. Resuelto escuchando, activando el protocolo y actuando sobre la organización." },
  ],
  resolveText:
    `<p>Con el protocolo activo, la evaluación psicosocial en marcha, la carga repartida y el apoyo del equipo, <b>Elena deja de estar sola</b>. Pablo, por fin, habla. El acoso no es 'cosa de la víctima': es un riesgo laboral que se previene.</p>
     <div class="epi-callout">En el expediente de la 'optimización', otra tarjeta: <b>09</b>. Y al pie de todas las medidas, por fin, una firma legible.</div>`,
};
